import React, { useCallback } from 'react';
import {
  Box,
  Checkbox,
  FormControlLabel,
  FormHelperText,
  Grid,
} from '@mui/material';
import { Controller, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { RichTextEditor } from 'components/RichTextEditor';
import { makeStyles } from 'tss-react/mui';
import { EvacZoneStatus, RegionEvacZoneStyle } from 'shared/types';
import {
  EVAC_ZONE_LABEL_TRANS_KEYS,
  EVAC_ZONE_STATUS_ENABLED,
} from '../../../../constants';

const useStyles = makeStyles()(() => ({
  editor: {
    minHeight: 42,
  },
}));

type CustomEvacuationLevelNotesProps = {
  customFieldName: string;
  customEvacZones: boolean;
  customEvacZoneString?: string;
  zoneStyles: RegionEvacZoneStyle[];
  level: EvacZoneStatus;
  hasEvacZones: boolean;
  toggleCustomZone: (enabled: boolean) => void;
  // todo: remove and default to true when the old evac zone editor is removed
  hideWhenDisabled?: boolean;
  disabled?: boolean;
};

export const CustomEvacuationLevelNotes = ({
  customFieldName,
  customEvacZones,
  customEvacZoneString,
  zoneStyles,
  level,
  hasEvacZones,
  toggleCustomZone,
  hideWhenDisabled = false,
  disabled = false,
}: CustomEvacuationLevelNotesProps): JSX.Element | null => {
  const { t } = useTranslation();
  const { classes } = useStyles();
  const { control, formState } = useFormContext();

  const editable =
    (!hasEvacZones || customEvacZones) && !formState.isSubmitting && !disabled;
  const shouldHide = hideWhenDisabled && !customEvacZones && hasEvacZones;

  const handleCheckboxChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>): void => {
      toggleCustomZone(event.target.checked);
    },
    [toggleCustomZone],
  );

  // Find first zone style that has the level enabled, if no styles have the level enabled, don't render the component.
  // We are doing the check here to handle cases where the component is rendered in a context that is supporting both
  // ready_set_go and ready_set_shelter as possible styles. (e.g. on the board of CA and OR)
  const zoneStyle = zoneStyles.find(
    (style) => EVAC_ZONE_STATUS_ENABLED[style][level],
  );
  if (!zoneStyle) {
    return null;
  }

  return (
    <>
      {hasEvacZones && (
        <Grid size={12} sx={{ mt: -2 }}>
          <FormControlLabel
            control={
              <Checkbox
                name={customFieldName}
                checked={Boolean(customEvacZones)}
                onChange={handleCheckboxChange}
                disabled={disabled}
              />
            }
            label={t(
              `geoEventEvacuations.inputs.${customFieldName}.enableCustom`,
            )}
          />
        </Grid>
      )}
      <Grid size={12}>
        <Controller
          name={customFieldName}
          control={control}
          render={({ field, fieldState }): JSX.Element => (
            <Box
              // Use CSS to hide the editor so the RichTextEditor doesn't unmount -> remount and display an outdated initial value
              sx={{ display: shouldHide ? 'none' : 'auto' }}
            >
              <RichTextEditor
                id={`field-control-evacuation-${level}`}
                data-testid={`evacuation-${level}`}
                label={t(EVAC_ZONE_LABEL_TRANS_KEYS[zoneStyle][level])}
                initialValue={customEvacZoneString || ''}
                onChange={field.onChange}
                editable={editable}
                error={Boolean(fieldState.error)}
                editorClassName={classes.editor}
                onBlur={field.onBlur}
              />
              {Boolean(fieldState.error) && (
                <FormHelperText
                  error
                  sx={{ marginLeft: 1.75, marginRight: 1.75 }}
                >
                  {fieldState.error?.message}
                </FormHelperText>
              )}
            </Box>
          )}
        />
      </Grid>
    </>
  );
};
